import type { JDSkill } from "@/api/jd-queries";

/**
 * How a JD's related technical share divides across its stacks — DESIGN §20.9. Each mapped technical
 * skill adds its importance's weight to its stack (core/jd.py:stack_weights) and the share is split by
 * those weights (core/picking.py:split_counts), so a stack with more Must skills gets more questions.
 * Mirrored here so Interview mix can say where each question comes from, unsaved edits included.
 */

// The shipped default (migration 0016), used only if the setting cannot be read.
const DEFAULT_WEIGHTS: Record<string, number> = { must: 3, nice: 2, other: 1 };

/** "must=3, nice=2, other=1", as `jd_importance_weights` holds it. */
export function importanceWeights(raw: string | undefined): Record<string, number> {
  const out: Record<string, number> = { ...DEFAULT_WEIGHTS };
  for (const part of (raw ?? "").split(",")) {
    const [key, value] = part.split("=").map((p) => p.trim());
    if (key && value && Number.isFinite(Number(value))) out[key] = Number(value);
  }
  return out;
}

/**
 * `total` related questions across the JD's stacks: proportional, floored, the remainder to the
 * heaviest — the picker sorts stacks heaviest first (picker/jd.py:resolve) and split_counts gives the
 * remainder to the first. Stacks whose share rounds to none are left out, as the picker skips them.
 */
export function stackSplit(
  total: number,
  skills: JDSkill[],
  weights: Record<string, number>,
): [string, number][] {
  const byStack = new Map<string, number>();
  for (const sk of skills) {
    if (sk.category !== "technical" || sk.status !== "mapped" || !sk.stack_path) continue;
    const w = weights[sk.importance] ?? 0;
    if (w > 0) byStack.set(sk.stack_path, (byStack.get(sk.stack_path) ?? 0) + w);
  }
  const keys = [...byStack.keys()].sort(
    (a, b) => (byStack.get(b) ?? 0) - (byStack.get(a) ?? 0) || a.localeCompare(b),
  );
  const scale = keys.reduce((n, k) => n + (byStack.get(k) ?? 0), 0);
  if (total <= 0 || scale === 0) return [];
  const out = keys.map((k) => [k, Math.floor((total * (byStack.get(k) ?? 0)) / scale)] as [string, number]);
  const first = out[0];
  if (first) first[1] += total - out.reduce((n, [, c]) => n + c, 0);
  return out.filter(([, n]) => n > 0);
}
